import { useState, useRef, useEffect } from 'react';

export default function SelfieCapture({ onCapture, onSkip }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [captured, setCaptured] = useState(null);
  const [error, setError] = useState('');
  const [cameraReady, setCameraReady] = useState(false);

  useEffect(() => {
    let mounted = true;
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: 640, height: 480 },
        });
        if (!mounted) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          setCameraReady(true);
        }
      } catch (err) {
        if (mounted) setError('Camera access denied. Please allow camera access and try again.');
      }
    };
    startCamera();
    return () => {
      mounted = false;
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  const takePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setCaptured(dataUrl);
  };

  const retake = () => setCaptured(null);

  const confirm = () => {
    if (captured) onCapture(captured);
  };

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorBox}>
          <p style={{ color: '#ef4444', fontWeight: 600 }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Identity Photo</h3>
      <p style={styles.desc}>
        You have been randomly selected for identity verification. Please take a clear photo of yourself.
      </p>

      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {!captured ? (
        <div style={styles.cameraArea}>
          <video ref={videoRef} autoPlay playsInline muted style={styles.video} />
          <button
            onClick={takePhoto}
            disabled={!cameraReady}
            className="btn btn-primary"
            style={{ ...styles.captureBtn, opacity: cameraReady ? 1 : 0.5 }}
          >
            {cameraReady ? 'Take Photo' : 'Starting camera...'}
          </button>
        </div>
      ) : (
        <div style={styles.previewArea}>
          <img src={captured} alt="Selfie preview" style={styles.preview} />
          <div style={styles.previewBtns}>
            <button className="btn btn-ghost" onClick={retake} style={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}>
              Retake
            </button>
            <button className="btn btn-primary" onClick={confirm} style={{ background: '#b39e89', border: 'none' }}>
              Confirm Photo
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { textAlign: 'center' },
  title: { fontFamily: 'var(--font-display)', fontSize: 20, color: 'white', marginBottom: 8 },
  desc: { fontSize: 14, color: 'rgba(255,255,255,0.7)', marginBottom: 20 },
  errorBox: { background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 12, padding: 20 },
  cameraArea: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 },
  video: { width: '100%', maxWidth: 400, borderRadius: 12, border: '2px solid rgba(255,255,255,0.2)', background: '#000' },
  captureBtn: { background: '#b39e89', border: 'none', fontWeight: 700, padding: '12px 32px' },
  previewArea: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 },
  preview: { width: '100%', maxWidth: 400, borderRadius: 12, border: '2px solid rgba(255,255,255,0.2)' },
  previewBtns: { display: 'flex', gap: 12 },
};
